import { useEffect, useRef, useState } from "react";
import { api, errorText } from "@/lib/api";

const EMPTY = { type: null, bad: "", opposite: "", goal: "", small: "", when: "", blocked: null, obstacle: "", avoid: "" };

const QUESTIONS = {
  cambiar: [
    ["bad", "¿Qué quieres cambiar? ¿Cuál es tu mal hábito?", "dormirme tarde", true],
    ["opposite", "¿Cómo te gustaría que fuera? ¿Cuál sería lo contrario?", "dormirme temprano", false],
    ["small", "¿Cuál sería la versión más pequeña con la que podrías empezar mañana?", "dormirme a las 11", true],
  ],
  nuevo: [
    ["goal", "¿Qué quieres lograr?", "leer más", true],
    ["small", "¿Cuál es la versión más simple de ese objetivo que puedas empezar mañana?", "leer 5 páginas de un libro", true],
    ["when", "¿En qué momento del día te resultaría más fácil hacerlo?", "por la noche", true],
  ],
};

const isComplete = (a) => a.type && QUESTIONS[a.type].every(([k, , , req]) => !req || a[k].trim())
  && a.blocked !== null && (!a.blocked || a.avoid.trim());

const fallback = (a) => {
  const parts = a.type === "cambiar" ? [a.small] : [a.when, a.small];
  if (a.blocked) parts.push(a.avoid);
  const text = parts.map((p) => p.trim()).filter(Boolean).join(" · ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const Num = ({ n }) => <span className="font-hand text-[28px] leading-none text-acento-texto w-7 shrink-0">{n}</span>;

const Question = ({ n, label, children }) => (
  <div className="flex gap-3">
    <Num n={n} />
    <div className="flex-1 min-w-0">
      <p className="text-[15px] font-medium text-tinta mb-2">{label}</p>
      {children}
    </div>
  </div>
);

const TypeCard = ({ active, title, desc, onClick, testId }) => (
  <button type="button" onClick={onClick} aria-pressed={active} data-testid={testId}
    className={`text-left rounded-[24px] p-5 border-[1.5px] transition-colors ${active ? "border-tinta bg-hoja" : "border-linea bg-superficie hover:bg-resaltado"}`}>
    <p className="font-serif text-[22px] text-tinta">{title}</p>
    <p className="text-[14px] text-sec mt-1">{desc}</p>
  </button>
);

export const HabitForm = ({ initial, onSaved, onCancel }) => {
  const [a, setA] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [habit, setHabit] = useState(initial?.habit || "");
  const [edited, setEdited] = useState(!!initial);
  const [status, setStatus] = useState("");
  const [care, setCare] = useState("");
  const [error, setError] = useState("");
  const req = useRef(0);
  const complete = isComplete(a);
  const set = (k) => (e) => setA((p) => ({ ...p, [k]: e.target.value }));

  const redraft = async () => {
    const id = ++req.current;
    setStatus("Remanso está redactando tu hábito…");
    try {
      const { data } = await api.post("/habits/draft", a);
      if (id !== req.current) return;
      if (data.care) { setCare(data.text); setHabit(""); } else { setCare(""); setHabit(data.text); }
      setStatus("");
    } catch {
      if (id !== req.current) return;
      setHabit(fallback(a));
      setStatus("Escríbelo en primera persona, como si ya lo hicieras.");
    }
  };

  useEffect(() => {
    if (!complete || edited) return undefined;
    const t = setTimeout(redraft, 800);
    return () => clearTimeout(t);
  }, [JSON.stringify(a), complete, edited]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setError("");
    try {
      const body = { ...a, habit: habit.trim() };
      const { data } = initial ? await api.put(`/habits/${initial.id}`, body) : await api.post("/habits", body);
      onSaved(data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="max-w-2xl" data-testid="habit-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <TypeCard active={a.type === "cambiar"} title="Cambiar un mal hábito" desc="Algo que haces hoy y quieres dejar"
          onClick={() => setA((p) => ({ ...p, type: "cambiar" }))} testId="habit-type-cambiar" />
        <TypeCard active={a.type === "nuevo"} title="Crear un hábito nuevo" desc="Algo que todavía no haces y quieres empezar"
          onClick={() => setA((p) => ({ ...p, type: "nuevo" }))} testId="habit-type-nuevo" />
      </div>
      {a.type && (
        <div className="mt-10 flex flex-col gap-7">
          {QUESTIONS[a.type].map(([k, label, eg], i) => (
            <Question key={k} n={i + 1} label={label}>
              <input className="field" value={a[k]} onChange={set(k)} placeholder={`Por ejemplo, ${eg}`} data-testid={`habit-q-${k}`} />
            </Question>
          ))}
          <Question n={4} label="¿Hay algo que te lo impida?">
            <div className="flex gap-2">
              {[[true, "Sí"], [false, "No"]].map(([v, l]) => (
                <button key={l} type="button" aria-pressed={a.blocked === v} onClick={() => setA((p) => ({ ...p, blocked: v }))}
                  className={`chip px-5 ${a.blocked === v ? "active" : ""}`} data-testid={`habit-blocked-${v ? "si" : "no"}`}>{l}</button>
              ))}
            </div>
          </Question>
          {a.blocked && (
            <Question n={5} label="¿Qué es?">
              <input className="field" value={a.obstacle} onChange={set("obstacle")} placeholder="Por ejemplo, quedarme viendo el celular" data-testid="habit-q-obstacle" />
              <p className="text-[15px] font-medium text-tinta mt-5 mb-2">¿Qué puedes hacer para evitarlo?</p>
              <input className="field" value={a.avoid} onChange={set("avoid")} placeholder="Por ejemplo, dejar el celular cargando en la cocina" data-testid="habit-q-avoid" />
            </Question>
          )}
        </div>
      )}
      {complete && (
        <div className="mt-10" data-testid="habit-result">
          <p className="eyebrow">Tu nuevo hábito</p>
          {care ? (
            <div className="mt-3 rounded-[24px] bg-riesgo text-tinta p-5 text-[16px] leading-relaxed" data-testid="habit-care-card">{care}</div>
          ) : (
            <textarea rows={3} value={habit} onChange={(e) => { setHabit(e.target.value); setEdited(true); }} data-testid="habit-text-input"
              className="mt-3 w-full rounded-[8px] border-[1.5px] border-dashed border-borde bg-hoja p-4 font-serif text-[24px] leading-snug text-tinta resize-none" />
          )}
          {status && <p className="text-[13px] text-meta mt-2" data-testid="habit-draft-status">{status}</p>}
          <button type="button" className="btn-text -ml-2 mt-2" onClick={() => { if (edited) { setEdited(false); setStatus("Remanso está redactando tu hábito…"); } else redraft(); }} data-testid="habit-redraft-link">
            Volver a escribirlo con mis respuestas
          </button>
        </div>
      )}
      {error && <p className="calm-note mt-6" role="status" data-testid="habit-form-error">{error}</p>}
      <div className="flex flex-wrap gap-3 mt-10">
        <button className="btn-primary" disabled={!complete || !habit.trim() || !!care} onClick={save} data-testid="habit-save-button">Guardar hábito</button>
        <button className="btn-secondary" onClick={onCancel} data-testid="habit-cancel-button">Volver</button>
      </div>
    </div>
  );
};
